import React, { useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    ScriptableContext,
} from 'chart.js';
import { MonitorOverviewProject } from 'domain/payload/response/monitor/MonitorOverviewResp';
import { useNavigate } from 'react-router-dom';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

interface MonitorOverviewCardProps {
    project: MonitorOverviewProject;
    onViewDetail: (projectId: string) => void;
}

const formatLastSubmitAt = (dateStr?: string | null): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

const getFlaggedTooltipText = (dateStr?: string | null): string => {
    if (!dateStr) {
        return 'Project tidak menerima result sejak awal';
    }
    const submitDate = new Date(dateStr);
    if (isNaN(submitDate.getTime())) {
        return `Project tidak menerima result sejak ${dateStr}`;
    }

    const day = String(submitDate.getDate()).padStart(2, '0');
    const month = String(submitDate.getMonth() + 1).padStart(2, '0');
    const year = submitDate.getFullYear();
    const formattedDate = `${day}-${month}-${year}`;

    const diffInMs = Date.now() - submitDate.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays > 0) {
        return `Project tidak menerima result sejak ${formattedDate} (${diffInDays} hari lalu)`;
    }
    return `Project tidak menerima result sejak ${formattedDate}`;
};

const MonitorOverviewCard: React.FC<MonitorOverviewCardProps> = ({
    project,
    onViewDetail,
}) => {
    const navigate = useNavigate();
    const isRunning = project.isRunning;
    const [showMenu, setShowMenu] = useState(false);
    const [showWarningTooltip, setShowWarningTooltip] = useState(false);
    const approved = project.approved || 0;
    const done = project.done || 0;
    const target = project.target || 0;
    const totalData = approved + done;
    const percentage = target > 0 ? Math.round((totalData / target) * 100) : 0;
    const approvedPercentage = target > 0 ? Math.round((approved / target) * 100) : 0;

    const lastSubmitAtRaw = project.lastSubmitAt;
    const formattedLastSubmitAt = formatLastSubmitAt(lastSubmitAtRaw);

    const checkIsFlagged = (): boolean => {
        if (!isRunning) return false;
        if (!lastSubmitAtRaw) return false;
        if (project.isFlagged !== undefined) return project.isFlagged;
        const submitDate = new Date(lastSubmitAtRaw);
        if (isNaN(submitDate.getTime())) return false;
        const diffInMs = Date.now() - submitDate.getTime();
        const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
        return diffInDays >= 7;
    };

    const isFlagged = checkIsFlagged();

    const hasApprovedData = approved > 0;
    const hasDoneData = done > 0;
    const hasData = hasApprovedData || hasDoneData;
    const todayValue = project.todayValue || 0;
    const yesterdayValue = project.yesterdayValue || 0;
    const lastWeekValue = project.lastWeekValue || 0;

    const todayVsYesterdayDiff = project.todayVsYesterday || 0;
    const todayVsLastWeekDiff = project.todayVsLastWeek || 0;

    const todayVsYesterdayPercent = yesterdayValue > 0
        ? Math.round((todayVsYesterdayDiff / yesterdayValue) * 100)
        : todayValue > 0 ? 100 : 0;

    const todayVsLastWeekPercent = lastWeekValue > 0
        ? Math.round((todayVsLastWeekDiff / lastWeekValue) * 100)
        : todayValue > 0 ? 100 : 0;

    const chartData = {
        labels: project.performanceData.map(d => d.label),
        datasets: [
            {
                label: 'Performance',
                data: project.performanceData.map(d => d.value),
                borderColor: isRunning ? '#2b75da' : '#94a3b8',
                backgroundColor: (context: ScriptableContext<'line'>) => {
                    const ctx = context.chart.ctx;
                    const gradient = ctx.createLinearGradient(0, 0, 0, 80);
                    if (isRunning) {
                        gradient.addColorStop(0, 'rgba(43, 117, 218, 0.3)');
                        gradient.addColorStop(1, 'rgba(43, 117, 218, 0.02)');
                    } else {
                        gradient.addColorStop(0, 'rgba(148, 163, 184, 0.25)');
                        gradient.addColorStop(1, 'rgba(148, 163, 184, 0.02)');
                    }
                    return gradient;
                },
                fill: true,
                tension: 0.4,
                pointBackgroundColor: isRunning ? '#2b75da' : '#94a3b8',
                pointBorderColor: '#ffffff',
                pointBorderWidth: 2,
                pointRadius: 3,
                pointHoverRadius: 6,
                borderWidth: 2,
                spanGaps: true,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: 'rgba(255,255,255,0.95)',
                titleColor: '#1e293b',
                bodyColor: '#475569',
                borderColor: '#e2e8f0',
                borderWidth: 1,
                cornerRadius: 8,
                padding: 10,
                callbacks: {
                    label: function (context: any) {
                        return `${context.raw} submissions`;
                    }
                }
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                ticks: {
                    font: {
                        size: 8,
                    },
                    color: '#94a3b8',
                    maxTicksLimit: 8,
                    maxRotation: 0,
                },
            },
            y: {
                grid: {
                    color: 'rgba(0,0,0,0.05)',
                    drawTicks: false,
                },
                ticks: {
                    font: {
                        size: 8,
                    },
                    color: '#94a3b8',
                    stepSize: 20,
                    maxTicksLimit: 5,
                },
                min: 0,
                suggestedMax: Math.max(...project.performanceData.map(d => d.value), 10) + 10,
            },
        },
        interaction: {
            intersect: false,
            mode: 'index' as const,
        },
        elements: {
            line: {
                tension: 0.4,
            },
            point: {
                hoverRadius: 6,
            },
        },
    };

    const getTrendIcon = (diff: number) => {
        if (diff > 0) return <i className="fa-solid fa-arrow-trend-up"></i>;
        if (diff < 0) return <i className="fa-solid fa-arrow-trend-down"></i>;
        return <i className="fa-solid fa-minus"></i>;
    };

    const getPercentDisplay = (percent: number) => {
        if (percent === 0) return '0%';
        if (percent > 0) return `+${percent}%`;
        return `${percent}%`;
    };

    const handleCardClick = () => {
        navigate(`/monitor/${project.projectId}`);
    };

    const handleViewDetail = (e: React.MouseEvent) => {
        e.stopPropagation();
        setShowMenu(false);
        onViewDetail(project.projectId);
    };

    const formatDataLabel = () => {
        if (!hasData) {
            return `<span style="color: #64748b;">0</span> / ${target}`;
        }
        if (target > 0) {
            if (done === 0) {
                return `<span style="color: #0d6efd; font-weight: 700;">${approved}</span> / ${target}`;
            }
            return `<span style="color: #0d6efd; font-weight: 700;">${approved}</span> <span style="color: #fd7e14; font-weight: 700;">(${done})</span> / ${target}`;
        }
        if (done === 0) {
            return `<span style="color: #0d6efd; font-weight: 700;">${approved}</span> / 0`;
        }
        return `<span style="color: #0d6efd; font-weight: 700;">${approved}</span> <span style="color: #fd7e14; font-weight: 700;">(${done})</span> / 0`;
    };

    const formatPercentageLabel = () => {
        if (!hasData) {
            return `<span style="color: #64748b; font-weight: 700;">0%</span>`;
        }
        if (target > 0) {
            if (approvedPercentage === percentage) {
                return `<span style="color: #0d6efd; font-weight: 700;">${approvedPercentage}%</span>`;
            }
            return `<span style="color: #0d6efd; font-weight: 700;">${approvedPercentage}%</span> <span style="color: #64748b; font-weight: 700;">(${percentage}%)</span>`;
        }
        const fallbackPercent = totalData > 0 ? 100 : 0;
        return `<span style="color: #0d6efd; font-weight: 700;">${fallbackPercent}%</span>`;
    };

    return (
        <div
            className="card border-0 shadow-sm h-100 position-relative"
            style={{
                borderRadius: '16px',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                cursor: 'pointer',
                opacity: 1,
                backgroundColor: '#ffffff',
                zIndex: showWarningTooltip ? 100 : 1,
            }}
            onClick={handleCardClick}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.12)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
            }}
        >
            {showMenu && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 99,
                    }}
                    onClick={(e) => { e.stopPropagation(); setShowMenu(false); }}
                />
            )}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    zIndex: 100,
                    padding: '10px 12px',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    style={{
                        cursor: 'pointer',
                        fontSize: '1rem',
                        color: '#475569',
                        border: 'none',
                        borderRadius: '6px',
                        width: '30px',
                        height: '30px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background-color 0.15s ease, color 0.15s ease',
                        userSelect: 'none',
                        lineHeight: 1,
                        padding: 0,
                        background: 'transparent',
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowMenu(!showMenu);
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f1f5f9';
                        e.currentTarget.style.color = '#1e293b';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#475569';
                    }}
                >
                    <i className="fa-solid fa-ellipsis"></i>
                </button>

                {showMenu && (
                    <div
                        className="dropdown-menu show"
                        style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            minWidth: '150px',
                            borderRadius: '12px',
                            boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                            border: 'none',
                            zIndex: 1000,
                            padding: '4px 0',
                            marginTop: '4px',
                            backgroundColor: '#ffffff',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="dropdown-item d-flex align-items-center gap-2 py-2 px-3"
                            onClick={handleViewDetail}
                            style={{
                                borderRadius: '8px',
                                fontSize: '0.85rem',
                                transition: 'background-color 0.15s ease',
                                border: 'none',
                                background: 'transparent',
                                width: '100%',
                                textAlign: 'left',
                                color: '#1e293b',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f1f5f9';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            <i className="fa-solid fa-eye" style={{ color: '#0d6efd' }}></i>
                            <span>View Detail</span>
                        </button>
                    </div>
                )}
            </div>

            <div className="card-body p-4">
                <div className="mb-3">
                    <div className="d-flex align-items-center justify-content-between gap-2 me-4">
                        <h5 className="fw-bold text-dark mb-0" style={{ fontSize: '1.05rem', lineHeight: '1.3' }}>
                            {project.projectName}
                        </h5>
                        <span className={`status-badge ${isRunning ? 'status-running' : 'status-stopped'}`}>
                            {isRunning ? (
                                <>
                                    <span className="live-pulse-dot">
                                        <span className="pulse-ring"></span>
                                        <span className="solid-dot"></span>
                                    </span>
                                    <span>Running</span>
                                    {isFlagged && (
                                        <span
                                            style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
                                            onMouseEnter={(e) => {
                                                e.stopPropagation();
                                                setShowWarningTooltip(true);
                                            }}
                                            onMouseLeave={(e) => {
                                                e.stopPropagation();
                                                setShowWarningTooltip(false);
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <i
                                                className="fa-solid fa-triangle-exclamation text-warning ms-1"
                                                style={{ fontSize: '0.85rem', cursor: 'pointer' }}
                                            />
                                            {showWarningTooltip && (
                                                <div
                                                    style={{
                                                        position: 'absolute',
                                                        top: 'calc(100% + 8px)',
                                                        right: 0,
                                                        width: '230px',
                                                        backgroundColor: '#0f172a',
                                                        color: '#ffffff',
                                                        padding: '8px 12px',
                                                        borderRadius: '8px',
                                                        fontSize: '0.75rem',
                                                        lineHeight: '1.35',
                                                        whiteSpace: 'normal',
                                                        boxShadow: '0 10px 25px rgba(0,0,0,0.25)',
                                                        zIndex: 9999,
                                                        pointerEvents: 'none',
                                                        textAlign: 'left',
                                                        fontWeight: 400,
                                                    }}
                                                >
                                                    {getFlaggedTooltipText(lastSubmitAtRaw)}
                                                </div>
                                            )}
                                        </span>
                                    )}
                                </>
                            ) : (
                                <>
                                    <span className="stopped-dot"></span>
                                    <span>Stopped</span>
                                </>
                            )}
                        </span>
                    </div>
                </div>

                {/* Cluster Badge */}
                <div className="mb-2">
                    <span className="cluster-badge">
                        <i className="fa-solid fa-mountain cluster-badge-icon"></i>
                        {project.cluster ? project.cluster : 'Other'}
                    </span>
                </div>

                <div className="row g-2 mb-3">
                    <div className="col-6">
                        <div
                            className="d-flex flex-column p-3 rounded-3"
                            style={{
                                backgroundColor: isRunning ? '#f0f7ff' : '#f8f9fa',
                                border: `1px solid ${isRunning ? '#dbeafe' : '#e2e8f0'}`,
                                borderRadius: '12px'
                            }}
                        >
                            <span className="text-muted text-uppercase" style={{ fontSize: '0.55rem', letterSpacing: '0.3px' }}>
                                DATA HARI INI
                            </span>
                            <h4 className={`fw-bold mb-0 ${isRunning ? 'text-primary' : 'text-secondary'}`} style={{ fontSize: '1.5rem' }}>
                                {project.dataToday.toLocaleString()}
                            </h4>
                        </div>
                    </div>
                    <div className="col-6">
                        <div
                            className="d-flex flex-column p-3 rounded-3"
                            style={{
                                backgroundColor: isRunning ? '#f0f7ff' : '#f8f9fa',
                                border: `1px solid ${isRunning ? '#dbeafe' : '#e2e8f0'}`,
                                borderRadius: '12px'
                            }}
                        >
                            <span className="text-muted text-uppercase" style={{ fontSize: '0.55rem', letterSpacing: '0.3px' }}>
                                OFFICER HARI INI
                            </span>
                            <h4 className={`fw-bold mb-0 ${isRunning ? 'text-dark' : 'text-secondary'}`} style={{ fontSize: '1.5rem' }}>
                                {project.officerToday}
                            </h4>
                        </div>
                    </div>
                </div>

                <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="text-muted" style={{ fontSize: '0.7rem' }}>
                            Current Data vs Target
                        </span>
                        <span
                            className="fw-bold text-dark"
                            style={{ fontSize: '0.75rem' }}
                            dangerouslySetInnerHTML={{
                                __html: `${formatDataLabel()} | ${formatPercentageLabel()}`
                            }}
                        />
                    </div>
                    <div className="progress" style={{
                        height: '6px',
                        backgroundColor: '#d1d5db',
                        borderRadius: '10px',
                        display: 'flex',
                        overflow: 'hidden'
                    }}>
                        {target > 0 && hasData ? (
                            <>
                                {hasApprovedData && (
                                    <div
                                        className="progress-bar"
                                        style={{
                                            width: `${Math.min((approved / target) * 100, 100)}%`,
                                            backgroundColor: '#0d6efd',
                                            borderRadius: hasDoneData ? '10px 0 0 10px' : '10px',
                                            transition: 'width 0.6s ease',
                                        }}
                                    />
                                )}
                                {hasDoneData && (
                                    <div
                                        className="progress-bar"
                                        style={{
                                            width: `${Math.min((done / target) * 100, 100 - (approved / target) * 100)}%`,
                                            backgroundColor: '#fd7e14',
                                            borderRadius: hasApprovedData ? '0 10px 10px 0' : '10px',
                                            transition: 'width 0.6s ease',
                                        }}
                                    />
                                )}
                            </>
                        ) : (
                            <div className="progress-bar" style={{ width: '0%', backgroundColor: 'transparent' }} />
                        )}
                    </div>
                </div>

                <div className="mb-3">
                    <span className="text-muted d-block" style={{ fontSize: '0.55rem', letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                        Comparing last 60 minutes traffic vs baseline periods
                    </span>
                    <div className="d-flex gap-4 mt-2">
                        <div>
                            <span className="text-muted" style={{ fontSize: '0.7rem' }}>Today vs Yesterday</span>
                            <div className="d-flex align-items-center gap-2 mt-1">
                                <span className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>
                                    {todayValue}/{yesterdayValue}
                                </span>
                                <span
                                    className="px-2 py-0.5 rounded-pill fw-bold d-inline-flex align-items-center gap-1 text-nowrap"
                                    style={{
                                        fontSize: '0.65rem',
                                        backgroundColor: todayVsYesterdayDiff > 0
                                            ? '#e6f4ea'
                                            : todayVsYesterdayDiff < 0
                                                ? '#fce8e6'
                                                : '#f1f3f4',
                                        color: todayVsYesterdayDiff > 0
                                            ? '#137333'
                                            : todayVsYesterdayDiff < 0
                                                ? '#c5221f'
                                                : '#5f6368',
                                        padding: '2px 6px'
                                    }}
                                >
                                    {getTrendIcon(todayVsYesterdayDiff)}
                                    {getPercentDisplay(todayVsYesterdayPercent)}
                                </span>
                            </div>
                        </div>
                        <div>
                            <span className="text-muted" style={{ fontSize: '0.7rem' }}>Today vs Last week</span>
                            <div className="d-flex align-items-center gap-2 mt-1">
                                <span className="fw-bold text-dark" style={{ fontSize: '0.9rem' }}>
                                    {todayValue}/{lastWeekValue}
                                </span>
                                <span
                                    className="px-2 py-0.5 rounded-pill fw-bold d-flex align-items-center gap-1 text-nowrap"
                                    style={{
                                        fontSize: '0.65rem',
                                        backgroundColor: todayVsLastWeekDiff > 0
                                            ? '#e6f4ea'
                                            : todayVsLastWeekDiff < 0
                                                ? '#fce8e6'
                                                : '#f1f3f4',
                                        color: todayVsLastWeekDiff > 0
                                            ? '#137333'
                                            : todayVsLastWeekDiff < 0
                                                ? '#c5221f'
                                                : '#5f6368',
                                        padding: '2px 6px'
                                    }}
                                >
                                    {getTrendIcon(todayVsLastWeekDiff)}
                                    {getPercentDisplay(todayVsLastWeekPercent)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ height: '80px' }}>
                    <Line data={chartData} options={chartOptions} />
                </div>
                <div className="mt-2 text-center text-muted" style={{ fontSize: '0.7rem' }}>
                    last submit at {formattedLastSubmitAt}
                </div>
            </div>
        </div>
    );
};

export default MonitorOverviewCard;